import React, { useEffect, useRef, useState } from "react";
import { MessageSquareText, X, Send } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "tm_chat_session";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState(localStorage.getItem("tm_chat_name") || "");
  const [phone, setPhone] = useState(localStorage.getItem("tm_chat_phone") || "");
  const [email, setEmail] = useState(localStorage.getItem("tm_chat_email") || "");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThread = async (sid) => {
    try {
      const { data } = await api.get(`/chat/${sid}`);
      setMessages(data || []);
    } catch {}
  };

  useEffect(() => {
    if (session) loadThread(session);
  }, [session]);

  useEffect(() => {
    if (!open || !session) return;
    const iv = setInterval(() => loadThread(session), 8000);
    return () => clearInterval(iv);
  }, [open, session]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || !name.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat", {
        name, phone, email, message: text.trim(),
        session_id: session,
      });
      const sid = data.session_id;
      if (!session) {
        localStorage.setItem(STORAGE_KEY, sid);
        setSession(sid);
      }
      localStorage.setItem("tm_chat_name", name);
      if (phone) localStorage.setItem("tm_chat_phone", phone);
      if (email) localStorage.setItem("tm_chat_email", email);
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-widget-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-brand hover:bg-brand-hover text-white px-5 py-3 shadow-xl flex items-center gap-3 border border-brand transition-colors"
          aria-label="Open chat"
        >
          <MessageSquareText className="w-5 h-5" />
          <span className="font-display font-bold uppercase text-sm tracking-wide">Chat with Us</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm bg-surface border border-[#E2E8F0] shadow-2xl animate-slide-in-br flex flex-col"
          style={{ height: "min(560px, 80vh)" }}
        >
          <div className="bg-obsidian/80 backdrop-blur-md border-b border-[#E2E8F0] px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-mono-label">Direct Line</div>
              <div className="font-display font-bold">Chat with Trades Mechanical</div>
            </div>
            <button data-testid="chat-widget-close" onClick={() => setOpen(false)} className="text-[#475569] hover:text-slate-900 transition-colors" aria-label="Close chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          {!session && (
            <div className="p-4 space-y-3 border-b border-[#E2E8F0]">
              <p className="text-xs text-[#475569]">Your message goes straight to our team — we'll reply as fast as we can.</p>
              <input data-testid="chat-input-name" className="field" placeholder="Your name *" value={name} onChange={(e) => setName(e.target.value)} />
              <input data-testid="chat-input-phone" className="field" placeholder="Phone (recommended)" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <input data-testid="chat-input-email" className="field" placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <p className="text-xs text-[#94A3B8] text-center pt-8">Start the conversation — you'll get a reply as soon as possible.</p>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.from_owner ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[80%] px-3 py-2 text-sm ${m.from_owner ? "bg-[#E2E8F0] text-slate-900" : "bg-brand text-white"}`}>
                  {m.message}
                  <div className="text-[10px] opacity-70 mt-1 font-mono">
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="border-t border-[#E2E8F0] p-3 flex items-center gap-2">
            <input
              data-testid="chat-input-message"
              className="field flex-1"
              placeholder="Type a message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <button data-testid="chat-send-btn" type="submit" disabled={sending || !text.trim() || !name.trim()} className="btn-brand">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
